import {
  Button,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Select,
} from "antd";
import dayjs from "dayjs";
import "../../styles/pages/prism/prismPaymentForm.scss";
import PrismTruncatedText from "./PrismTruncatedText";
import {
  DEFAULT_REMARKS_ACCENT,
  REMARKS_ACCENT_OPTIONS,
} from "./prismPaymentUtils";

const PAYMENT_METHOD_OPTIONS = [
  { value: "credit_card", label: "Credit Card" },
  { value: "bank_transfer", label: "Bank Transfer" },
  { value: "paypal", label: "Paypal" },
  { value: "other", label: "Other" },
];

const PAYMENT_FIELD_HELP = Object.freeze({
  subscription:
    "Select the subscription receiving this payment. This field is locked when the form is opened from a subscription row.",
  amount: "Enter the amount recorded for this payment.",
  paidDate: "Select the actual date and time the payment was made.",
  paymentMethod: "Select the payment channel used for this transaction.",
  remarks:
    "Optionally add context that will appear in Payment History and exported reports.",
  remarksAccent:
    "Choose the on-screen highlight color for these remarks. Exported remark text is unchanged.",
});

const toDateTimeString = (value) => {
  if (!value) return null;
  if (dayjs.isDayjs(value)) return value.format("YYYY-MM-DD HH:mm:00");
  return dayjs(value).format("YYYY-MM-DD HH:mm:00");
};

const normalizeOptionalText = (value) => {
  if (typeof value !== "string") return value ?? null;

  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
};

const PrismPaymentForm = ({
  form,
  subscriptionOptions,
  loading,
  onCancel,
  onSubmit,
  mode = "add",
  subscriptionLocked = false,
}) => {
  const handleFinish = (values) => {
    onSubmit({
      subscription_id: values.subscription_id,
      amount: values.amount,
      date_paid: toDateTimeString(values.date_paid),
      payment_method: values.payment_method,
      remarks: normalizeOptionalText(values.remarks),
      remarks_accent: values.remarks_accent || DEFAULT_REMARKS_ACCENT,
    });
  };

  return (
    <Form
      className="prism-payment-form"
      form={form}
      layout="vertical"
      onFinish={handleFinish}
      initialValues={{
        payment_method: "other",
        remarks_accent: DEFAULT_REMARKS_ACCENT,
      }}
    >
      <Form.Item
        className="prism-payment-form__field--subscription"
        name="subscription_id"
        label="Subscription"
        tooltip={PAYMENT_FIELD_HELP.subscription}
        rules={[{ required: true, message: "Please select a subscription." }]}
      >
        <Select
          disabled={subscriptionLocked}
          showSearch
          placeholder="Select Subscription"
          options={subscriptionOptions}
          optionRender={(option) => (
            <PrismTruncatedText value={option?.label} />
          )}
          labelRender={({ label, value }) => (
            <PrismTruncatedText value={label ?? value} />
          )}
          filterOption={(input, option) =>
            (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
          }
        />
      </Form.Item>

      <Form.Item
        name="amount"
        label="Amount"
        tooltip={PAYMENT_FIELD_HELP.amount}
        rules={[
          { required: true, message: "Please enter the payment amount." },
          {
            type: "number",
            min: 0,
            message: "Amount must be zero or greater.",
          },
        ]}
      >
        <InputNumber min={0} style={{ width: "100%" }} />
      </Form.Item>

      <Form.Item
        name="date_paid"
        label="Paid Date"
        tooltip={PAYMENT_FIELD_HELP.paidDate}
        rules={[{ required: true, message: "Please select the paid date." }]}
      >
        <DatePicker
          format="YYYY-MM-DD HH:mm"
          popupClassName="prism-paid-date-picker"
          showTime={{
            title: "Time Paid",
            format: "HH:mm",
            showSecond: false,
          }}
          style={{ width: "100%" }}
        />
      </Form.Item>

      <Form.Item
        name="payment_method"
        label="Payment Method"
        tooltip={PAYMENT_FIELD_HELP.paymentMethod}
        rules={[{ required: true, message: "Please select a payment method." }]}
      >
        <Select options={PAYMENT_METHOD_OPTIONS} />
      </Form.Item>

      <Form.Item
        className="prism-payment-form__field--remarks"
        name="remarks"
        label="Remarks"
        tooltip={PAYMENT_FIELD_HELP.remarks}
      >
        <Input.TextArea placeholder="Optional payment notes" rows={2} />
      </Form.Item>

      <Form.Item
        name="remarks_accent"
        label="Remarks Accent"
        tooltip={PAYMENT_FIELD_HELP.remarksAccent}
      >
        <Select
          options={REMARKS_ACCENT_OPTIONS}
          optionRender={(option) => (
            <span className="prism-remarks-accent-option">
              <span
                aria-hidden="true"
                className={`prism-remarks-accent-swatch prism-remarks-accent-swatch--${option.value}`}
              />
              {option.label}
            </span>
          )}
        />
      </Form.Item>

      <Form.Item className="prism-payment-form__actions">
        <div className="prism-payment-form__action-buttons">
          <Button disabled={loading} onClick={onCancel}>
            Cancel
          </Button>
          <Button type="primary" htmlType="submit" loading={loading}>
            {mode === "edit" ? "Save Changes" : "Add Payment"}
          </Button>
        </div>
      </Form.Item>
    </Form>
  );
};

export default PrismPaymentForm;
